import type {
  FilterOption,
  InputFilterMenu as InputFilterMenuType,
} from '@/components/FilterMenu'
import type { DeploymentLabelOption } from '@/contexts/useApiaries'
import { HStack } from '@chakra-ui/react'
import type { ReactNode } from 'react'

import { ApprovalStateFilter } from './FilterGroups/ApprovalStateFilter'
import { DeploymentLabelFilter } from './FilterGroups/DeploymentLabelFilter'
import { HiveIdFilter } from './FilterGroups/HiveIdFilter'
import { HiveStateFilter } from './FilterGroups/HiveStateFilter'
import { IncludeDisabledFilter } from './FilterGroups/IncludeDisabledFilter'
import { OrganizationFilter } from './FilterGroups/OrganizationFilter'
import { ShareButton } from './FilterGroups/ShareButton'
import { SortFilter } from './FilterGroups/SortFilter'
import { TagsFilter } from './FilterGroups/TagsFilter'
import { TemplateFilter } from './FilterGroups/TemplateFilter'

interface FilterControlsProps {
  selectedOrgId: string | null
  handleOrgIdSearch: (value: string) => void

  selectedDeploymentLabel: DeploymentLabelOption | null
  handleDeploymentLabelChange: (option: DeploymentLabelOption | null) => void

  templatesData: { template_id: string; name?: string }[]
  selectedTemplate: FilterOption | null
  handleTemplateChange: (option: FilterOption | null) => void

  approvalStateOptions: FilterOption[]
  selectedApprovalState: FilterOption | null
  handleApprovalStateChange: (option: FilterOption | null) => void

  hiveStateOptions: FilterOption[]
  selectedHiveState: FilterOption | null
  handleHiveStateChange: (option: FilterOption | null) => void

  selectedHiveId: string | null
  handleHiveIdSearch: (value: string) => void

  tagOptions: FilterOption[]
  selectedTags: FilterOption[]
  setSelectedTags: (tags: FilterOption[]) => void

  sortOrderOptions: FilterOption[]
  selectedSortOption: FilterOption
  handleSortChange: (option: FilterOption | null) => void

  includeDisabled: boolean
  toggleIncludeDisabledChange: () => void

  setPage: (pageNumber: number | ((prev: number) => number)) => void

  hasActiveFilters: boolean
  copyFilterUrl?: () => void

  renderTagAndValue: <T>(
    option: FilterOption<T>,
    isSelected: boolean,
  ) => ReactNode
  InputFilterMenu: typeof InputFilterMenuType
}

export function FilterControls({
  selectedOrgId,
  handleOrgIdSearch,
  selectedDeploymentLabel,
  handleDeploymentLabelChange,
  templatesData,
  selectedTemplate,
  handleTemplateChange,
  approvalStateOptions,
  selectedApprovalState,
  handleApprovalStateChange,
  hiveStateOptions,
  selectedHiveState,
  handleHiveStateChange,
  selectedHiveId,
  handleHiveIdSearch,
  tagOptions,
  selectedTags,
  setSelectedTags,
  sortOrderOptions,
  selectedSortOption,
  handleSortChange,
  includeDisabled,
  toggleIncludeDisabledChange,
  setPage,
  hasActiveFilters,
  copyFilterUrl,
  renderTagAndValue,
  InputFilterMenu,
}: FilterControlsProps) {
  return (
    <HStack width="100%" data-testid="filter-bar" zIndex={3}>
      <HStack gap={1} alignItems="flex-start" data-testid="filters">
        <OrganizationFilter
          selectedValue={selectedOrgId}
          onSearch={handleOrgIdSearch}
          InputFilterMenu={InputFilterMenu}
        />

        <DeploymentLabelFilter
          selectedOption={selectedDeploymentLabel}
          onChange={handleDeploymentLabelChange}
        />

        <TemplateFilter
          templatesData={templatesData}
          selectedOption={selectedTemplate}
          onChange={handleTemplateChange}
          renderMenuItem={renderTagAndValue}
        />

        <ApprovalStateFilter
          options={approvalStateOptions}
          selectedOption={selectedApprovalState}
          onChange={handleApprovalStateChange}
        />

        <HiveStateFilter
          options={hiveStateOptions}
          selectedOption={selectedHiveState}
          onChange={handleHiveStateChange}
        />

        <HiveIdFilter
          selectedValue={selectedHiveId}
          onSearch={handleHiveIdSearch}
          InputFilterMenu={InputFilterMenu}
        />

        <TagsFilter
          options={tagOptions}
          selectedTags={selectedTags}
          setSelectedTags={setSelectedTags}
          setPage={setPage}
        />

        <SortFilter
          options={sortOrderOptions}
          selectedOption={selectedSortOption}
          onChange={handleSortChange}
        />
      </HStack>

      <HStack justifyContent="space-between" width="100%">
        <IncludeDisabledFilter
          includeDisabled={includeDisabled}
          onToggle={toggleIncludeDisabledChange}
        />

        <ShareButton
          isVisible={hasActiveFilters && !!copyFilterUrl}
          onClick={
            copyFilterUrl ||
            (() => {
              /* no-op */
            })
          }
        />
      </HStack>
    </HStack>
  )
}
